// src/components/results/Nom035BarChart.jsx

import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
  Cell
} from "recharts";
import "./Nom035BarChart.css";

const splitLabel = (value) => {
  const words = String(value).split(" ");
  if (words.length < 2) return [value];

  let bestIndex = 1;
  let smallestDifference = Infinity;
  for (let index = 1; index < words.length; index += 1) {
    const firstLine = words.slice(0, index).join(" ");
    const secondLine = words.slice(index).join(" ");
    const difference = Math.abs(firstLine.length - secondLine.length);
    if (difference < smallestDifference) {
      bestIndex = index;
      smallestDifference = difference;
    }
  }
  return [words.slice(0, bestIndex).join(" "), words.slice(bestIndex).join(" ")];
};

function TwoLineTick({ x, y, payload }) {
  const lines = splitLabel(payload.value);
  return (
    <text x={x} y={y + 10} textAnchor="middle" fill="var(--color-azul-oscuro)" fontSize={11}>
      {lines.map((line, index) => (
        <tspan key={line} x={x} dy={index === 0 ? 0 : 14}>{line}</tspan>
      ))}
    </text>
  );
}

/**
 * Nom035BarChart
 * Like the generic barChartComponent but specialized for NOM-035:
 * - Forces integer Y-axis
 * - Always shows all X labels (interval=0)
 * - Applies a fixed color palette in order
 * - No title prop (handled by wrapper)
 *
 * Props:
 * - data: [{ name: string, value: number }, ...] (always length=5 for risk levels)
 * - colors?: [string, ...] optional override, default is the 5-color NOM-035 palette
 */
const Nom035BarChart = ({
  data,
  twoLineLabels = false,
  colors = [
    "var(--color-azul)",       // Nulo
    "var(--color-delilife)",   // Bajo
    "var(--color-amarillo)",   // Medio
    "#FFA500",                 // Alto
    "red"                      // Muy alto
  ]
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  return (
  <div className="nom035-bar-chart-container">
    <ResponsiveContainer width="100%" height={twoLineLabels ? 300 : (isMobile ? 320 : 300)}>
      <BarChart
        data={data}
        barCategoryGap={20}
        margin={twoLineLabels ? { top: 20, right: 20, bottom: 45, left: 20 } : (isMobile ? { top: 10, right: 10, bottom: 90, left: 0 } : { top: 20, right: 20, bottom: 40, left: 20 })}
      >
        <CartesianGrid stroke="var(--color-azul-claro)" strokeDasharray="3 3" />

        <XAxis
          dataKey="name"
          stroke="var(--color-azul-oscuro)"
          tick={twoLineLabels ? <TwoLineTick /> : { fill: "var(--color-azul-oscuro)" }}
          interval={0}
          angle={!twoLineLabels && isMobile ? -90 : 0}
          textAnchor={!twoLineLabels && isMobile ? "end" : "middle"}
          height={twoLineLabels ? 58 : (isMobile ? 100 : 60)}
          tickMargin={!twoLineLabels && isMobile ? 10 : 0}
        />

        <YAxis
          stroke="var(--color-azul-oscuro)"
          tick={{ fill: "var(--color-azul-oscuro)" }}
          allowDecimals={false}
        />

        <Tooltip
          contentStyle={{
            backgroundColor: "white",
            border: "1px solid var(--color-gris-sec)"
          }}
          itemStyle={{ color: "var(--color-azul-oscuro)" }}
          cursor={{ fill: "rgba(0, 190, 156, 0.1)" }}
        />

        <Bar dataKey="value" barSize={30}>
          <LabelList
            dataKey="value"
            position="inside"
            fill="white"
            style={{ fontWeight: "bold" }}
          />
          {data.map((_, idx) => (
            <Cell key={idx} fill={colors[idx % colors.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
  );
};

export default Nom035BarChart;
